/**
 * ActionRow - Wave 0 (2026-05-27). Consistent operator-action button
 * cluster used across Decisions, Findings, Breaches, etc. Each action
 * is gated on permission/license and shows a pending state during
 * async execution. Standardises the look so the SOC operator learns
 * one pattern.
 *
 * Usage:
 *   <ActionRow
 *     actions={[
 *       { id: 'ack', label: 'Acknowledge', icon: CheckCircle2, onClick: onAck, kind: 'default' },
 *       { id: 'suppress', label: 'Suppress', icon: EyeOff, onClick: onSup, kind: 'warning' },
 *       { id: 'kill', label: 'Kill session', icon: Power, onClick: onKill, kind: 'critical', requireConfirm: true },
 *     ]}
 *   />
 */
import { useState, type ComponentType } from 'react';
import { Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { useMotionLevel } from '@/lib/motion-level';
import { cn } from '@/lib/cn';

type ActionKind = 'default' | 'success' | 'warning' | 'critical' | 'ghost';

export interface RowAction {
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  onClick: () => Promise<void> | void;
  kind?: ActionKind;
  disabled?: boolean;
  /** If true, show a small inline confirm prompt before invoking. */
  requireConfirm?: boolean;
  /** Disabled-reason shown on hover (e.g. "Requires Security Operator role") */
  disabledReason?: string;
}

interface ActionRowProps {
  actions: RowAction[];
  className?: string;
  size?: 'sm' | 'default';
}

const KIND_CLASSES: Record<ActionKind, string> = {
  default: '',
  success: 'border-success/40 hover:bg-success/10 text-success',
  warning: 'border-warning/40 hover:bg-warning/10 text-warning',
  critical: 'border-destructive/40 hover:bg-destructive/10 text-destructive',
  ghost: 'border-transparent',
};

export function ActionRow({
  actions,
  className,
  size = 'sm',
}: ActionRowProps): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const level = useMotionLevel();

  async function run(action: RowAction): Promise<void> {
    if (action.requireConfirm && confirming !== action.id) {
      setConfirming(action.id);
      setTimeout(() => setConfirming((c) => (c === action.id ? null : c)), 4000);
      return;
    }
    setConfirming(null);
    setBusy(action.id);
    try {
      await action.onClick();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {actions.map((a) => {
        const Icon = a.icon;
        const isBusy = busy === a.id;
        const isConfirming = confirming === a.id;
        const kindCls = KIND_CLASSES[a.kind ?? 'default'];
        return (
          <motion.div
            key={a.id}
            initial={level === 'full' ? { opacity: 0, scale: 0.96 } : { opacity: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={level === 'full' ? { type: 'spring', stiffness: 320, damping: 24 } : { duration: 0.12 }}
          >
            <Button
              variant={a.kind === 'critical' ? 'destructive' : 'glass'}
              size={size}
              disabled={a.disabled || busy !== null}
              onClick={() => void run(a)}
              title={a.disabled ? a.disabledReason ?? '' : undefined}
              className={cn(kindCls, isConfirming && 'ring-2 ring-warning/60')}
            >
              {isBusy ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : Icon ? (
                <Icon className="h-3 w-3 mr-1" />
              ) : null}
              {isConfirming ? `Confirm ${a.label.toLowerCase()}?` : a.label}
            </Button>
          </motion.div>
        );
      })}
    </div>
  );
}
